/* ============================================================
   RSI USED RACKING QUOTE BUILDER — PHASE 1 COMPANION
   assets/rsi-uqb-phase1.js
   Loads AFTER rsi-used-racking-quote-builder.js. Completely
   self-contained: reads the DOM only, never touches the main
   script's state, so the two can't fight. Provides:
     P1-A  Quote reference number (form field + receipt) — generated
           per session and submitted, but not displayed pre-submit
     P1-B  Working-hours SLA clock (button note + receipt slot)
     P1-C  Collapsed-step summaries in accordion headers
     P1-D  Receipt population on the success screen
     P1-E  Webhook POST to n8n on submit, held to complete before the
           native form submission proceeds (see the submit handler
           below for why — not a plain fire-and-forget beacon)
   ============================================================ */
(function () {
  'use strict';

  /* ---- CONFIG — the only things you should need to edit ----
     webhookUrl: confirmed live and working — endpoint returns 200 to
     both a direct curl and a same-page fetch() with no CORS/CSP issues.
     Leave '' to disable P1-E entirely (e.g. while testing a new host
     that hasn't been reviewed yet). */
  var CFG = {
    webhookUrl: 'https://n8n.revsecure.co/webhook/ris-quote-builder',
    openHour: 8,          // 8am
    closeHour: 17,        // 5pm
    openDays: [0,1,2,3,4,5,6], // open 7 days (0 = Sunday)
    slaMinutes: 60,
    storageKey: 'rsiUqbPhase1Receipt'
  };

  var root = document.querySelector('[data-rsi-uqb]');
  if (!root) return;
  var form = document.getElementById('UsedRackingQuoteBuilder');

  /* ============================================================
     P1-A — REFERENCE NUMBER
     Generated once per visit, persisted for the session so a
     validation round-trip doesn't change it.
     ============================================================ */
  function getRef() {
    try {
      var existing = sessionStorage.getItem('rsiUqbRef');
      if (existing) return existing;
    } catch (e) {}
    var ref = 'RIS-Q-' + Date.now().toString(36).toUpperCase().slice(-6);
    try { sessionStorage.setItem('rsiUqbRef', ref); } catch (e) {}
    return ref;
  }
  var quoteRef = getRef();
  var refField = root.querySelector('[data-rsi-p1-ref-field]');
  if (refField) refField.value = quoteRef;

  /* ============================================================
     P1-B — SLA CLOCK (working-hours aware)
     Returns the promise text used at the button, in the receipt,
     and in the webhook payload (so the SMS matches the page).
     ============================================================ */
  function fmtTime(d) {
    return d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' }).replace(' ', '');
  }
  function slaPromise(nowMs) {
    var now = new Date(nowMs || Date.now());
    var h = now.getHours();
    var isOpenDay = CFG.openDays.indexOf(now.getDay()) !== -1;
    var due = new Date(now.getTime() + CFG.slaMinutes * 60000);

    if (isOpenDay && h >= CFG.openHour && h < CFG.closeHour &&
        (due.getHours() < CFG.closeHour || (due.getHours() === CFG.closeHour && due.getMinutes() === 0))) {
      return { text: 'before ' + fmtTime(due), dueIso: due.toISOString(), inHours: true };
    }
    // Outside hours (or too close to closing): promise first thing next opening.
    var next = new Date(now);
    if (!(isOpenDay && h < CFG.openHour)) next.setDate(next.getDate() + 1);
    while (CFG.openDays.indexOf(next.getDay()) === -1) next.setDate(next.getDate() + 1);
    next.setHours(CFG.openHour, 30, 0, 0);
    var label = (next.toDateString() === now.toDateString()) ? 'today' : 'tomorrow';
    return { text: 'by ' + fmtTime(next) + ' ' + label, dueIso: next.toISOString(), inHours: false };
  }
  function paintSla() {
    var p = slaPromise();
    var inline = root.querySelector('[data-rsi-p1-sla-inline]');
    if (inline) inline.textContent = p.inHours ? ('within 1 hour — ' + p.text) : p.text;
    return p;
  }
  paintSla();
  setInterval(paintSla, 60000); // keep honest as the clock ticks

  /* ============================================================
     P1-C — COLLAPSED-STEP SUMMARIES
     Writes the customer's choices into each accordion header so
     the closed page reads as the spec.
     ============================================================ */
  function fieldEl(name) { return root.querySelector('[data-rsi-uqb-field="' + name + '"]'); }
  function selText(name) {
    var el = fieldEl(name);
    return (el && el.selectedOptions && el.selectedOptions[0]) ? el.selectedOptions[0].text : '';
  }
  function numVal(name) { var el = fieldEl(name); return el ? el.value : ''; }
  function radioText(name) {
    var el = root.querySelector('[data-rsi-uqb-field="' + name + '"]:checked');
    if (!el) return '';
    var span = el.parentElement && el.parentElement.querySelector('span');
    return span ? span.textContent.trim() : el.value;
  }
  function mmShort(name) { return selText(name).replace('mm', ''); }

  var STEP_TEXT = {
    '1': function () { return radioText('requestType'); },
    '2': function () {
      var m = radioText('matchingExisting');
      var brandBlock = root.querySelector('[data-rsi-uqb-brand-block]');
      var showBrand = brandBlock && !brandBlock.hidden;
      return m + (showBrand ? ' · ' + selText('brand') : '');
    },
    '3-complete': function () {
      return numVal('cr.bays') + ' bays · ' + mmShort('cr.frameHeightMm') + '×' + mmShort('cr.frameDepthMm') + 'mm · ' +
             selText('cr.beamLengthKey') + ' beams · ' + selText('cr.beamLevels') + ' levels';
    },
    '3-frames': function () { return numVal('fo.quantity') + ' frames · ' + mmShort('fo.frameHeightMm') + '×' + mmShort('fo.frameDepthMm') + 'mm'; },
    '3-beams': function () { return numVal('bo.beamPairQuantity') + ' pairs · ' + selText('bo.beamLengthKey'); },
    '3-decking': function () { return selText('do.type') + ' · ' + numVal('do.positionQuantity') + ' levels'; },
    '3-accessories': function () {
      var bits = [];
      if (+numVal('ao.floorBoltQuantity')) bits.push(numVal('ao.floorBoltQuantity') + ' bolts');
      if (+numVal('ao.protectorQuantity')) bits.push(numVal('ao.protectorQuantity') + ' protectors');
      if (+numVal('ao.loadNoticeQuantity')) bits.push(numVal('ao.loadNoticeQuantity') + ' load notices');
      return bits.join(' · ');
    },
    '3-not-sure': function () { return ''; },
    '4': function () {
      var bits = [];
      var deck = selText('cr.decking'); if (deck && deck !== 'No decking') bits.push(deck);
      if (radioText('cr.floorBolts') === 'Yes') bits.push('floor fixings');
      var prot = selText('cr.frameProtectors'); if (prot && prot !== 'No') bits.push('leg protectors');
      if (radioText('cr.loadNotice') === 'Yes') bits.push('load notice');
      return bits.length ? bits.join(' · ') : 'No extras';
    },
    '5': function () {
      var pc = (fieldEl('delivery.postcode') || {}).value || '';
      return selText('delivery.requirement') + (pc ? ' · ' + pc.toUpperCase() : '') + ' · ' + selText('delivery.timeframe');
    },
    '6': function () {
      var n = (fieldEl('customer.name') || {}).value || '';
      return n ? n : '';
    }
  };

  function paintStepSummaries() {
    var steps = root.querySelectorAll('[data-rsi-uqb-step]');
    Array.prototype.forEach.call(steps, function (step) {
      if (step.hidden) return;
      var key = step.getAttribute('data-rsi-uqb-step');
      var fn = STEP_TEXT[key];
      if (!fn) return;
      var legend = step.querySelector('.rsi-uqb__step-legend');
      if (!legend) return;
      var chip = legend.querySelector('.rsi-uqb__step-chosen');
      if (!chip) {
        chip = document.createElement('span');
        chip.className = 'rsi-uqb__step-chosen';
        legend.appendChild(chip);
      }
      var text = '';
      try { text = fn() || ''; } catch (e) {}
      chip.textContent = text;
      chip.hidden = !text || step.open; // only show on the collapsed header
    });
  }
  document.addEventListener('change', function () { setTimeout(paintStepSummaries, 50); });
  document.addEventListener('input', function () { setTimeout(paintStepSummaries, 50); });
  root.addEventListener('toggle', function () { setTimeout(paintStepSummaries, 0); }, true);
  setTimeout(paintStepSummaries, 250);

  /* ============================================================
     P1-D + P1-E — SUBMIT: snapshot the receipt, fire the beacon
     ============================================================ */
  function gatherPayload() {
    var summaryCard = root.querySelector('[data-rsi-uqb-summary-desktop]');
    var payloadField = root.querySelector('[data-rsi-uqb-payload-field]');
    var sla = slaPromise();
    var cb = root.querySelector('[data-rsi-p1-callback]:checked');
    return {
      ref: quoteRef,
      submittedAt: new Date().toISOString(),
      slaText: sla.text,
      slaDueIso: sla.dueIso,
      callbackPreference: cb ? cb.value : 'asap',
      name: (fieldEl('customer.name') || {}).value || '',
      company: (fieldEl('customer.company') || {}).value || '',
      email: (fieldEl('customer.email') || {}).value || '',
      phone: (fieldEl('customer.phone') || {}).value || '',
      postcode: (fieldEl('customer.postcode') || {}).value || '',
      deliveryPostcode: (fieldEl('delivery.postcode') || {}).value || '',
      requestType: radioText('requestType'),
      matchingExisting: radioText('matchingExisting'),
      guidePriceExVat: (root.querySelector('[data-rsi-uqb-hidden-ex-vat]') || {}).value || '',
      guidePriceIncVat: (root.querySelector('[data-rsi-uqb-hidden-inc-vat]') || {}).value || '',
      quotePayloadJson: payloadField ? payloadField.value : '',
      summaryText: summaryCard ? summaryCard.innerText : '',
      summaryHtml: summaryCard ? summaryCard.innerHTML : ''
    };
  }

  if (form) {
    var webhookHandled = false;
    form.addEventListener('submit', function (event) {
      // An earlier "submit" listener (the main script's own field
      // validation) already blocks invalid attempts via its own
      // event.preventDefault() — nothing valid to send in that case, and
      // nothing here should interfere with the browser showing those
      // validation errors, so bail out immediately.
      if (event.defaultPrevented) return;
      if (!CFG.webhookUrl || webhookHandled) return;

      var p = gatherPayload();
      // Snapshot for the receipt screen after the redirect.
      try { sessionStorage.setItem(CFG.storageKey, JSON.stringify(p)); } catch (e) {}

      /* Hold the native submission until the webhook call finishes (or a
         short ceiling elapses), instead of firing-and-forgetting via
         sendBeacon/keepalive fetch during the unload. sendBeacon is spec'd
         to survive navigation, but live testing against the production
         endpoint showed the request consistently getting cut off once the
         page began navigating for the native Shopify /contact submission
         (confirmed via network inspection: the beacon was dispatched but
         never completed) — a direct fetch from this same page, with no
         navigation racing it, delivered fine (200 OK). Blocking submission
         for up to 1.2s removes that race entirely for the normal case,
         while the ceiling guarantees a slow or unreachable webhook can
         never trap the customer's actual quote request. */
      event.preventDefault();
      webhookHandled = true;

      var proceeded = false;
      function proceed() {
        if (proceeded) return;
        proceeded = true;
        form.submit(); // Raw DOM method deliberately, not requestSubmit() —
                        // does not re-dispatch "submit", so this can't loop
                        // back into this same handler.
      }

      try {
        fetch(CFG.webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(p)
        }).catch(function () {}).then(proceed);
      } catch (e) {
        proceed();
        return;
      }
      setTimeout(proceed, 1200);
    });
  }

  /* "How would you like to proceed?" outcomes (assets/rsi-uqb-v2.js's
     CALLBACK_OPTS) fall into three modes with genuinely different "what
     happens next" copy — a customer who explicitly asked to be emailed
     rather than called shouldn't land on a receipt that still says
     "we'll call you" four separate times. CALL keeps the original,
     unedited copy byte-for-byte (still the default/majority path);
     EMAIL and PURCHASE get their own copy for the four spans the liquid
     template wraps for this (meta-note, slot-detail, step3, contact
     paragraph) — data-rsi-p1-slot itself (the time badge) is left alone,
     the existing SLA-based fallback already reads fine for all three
     modes ("within 1 working hour" is still roughly when the team acts,
     whichever channel they use). */
  var CONTACT_NAME_ATTR = 'data-rsi-uqb-v2-contact-name';
  var PHONE_DISPLAY = '01827 949222';
  var PHONE_HREF = 'tel:01827949222';
  // Matches assets/rsi-uqb-v2.js's own esc() — same merchant-controlled
  // value (section.settings.contact_name). Only needed for the innerHTML
  // target (contactPara) below — slotDetail/metaNote go through
  // textContent, which escapes on assignment, so passing this same
  // escaped string there would double-escape any "&" it contains.
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function receiptCopy(mode, contactName) {
    var contactNameHtml = esc(contactName);
    if (mode === 'email_quote') {
      return {
        metaNote: 'nothing more is needed from you — your firm quote is on its way by email.',
        slotDetail: contactName + ' emails your firm quote, confirming stock, final price and delivery.',
        step3: '<strong>By email</strong> — you get pricing and lead time confirmed in writing.',
        contactPara: '<strong>Your quote is with ' + contactNameHtml + '</strong> — check your inbox (and spam folder) for the firm quote. Need it faster? Call <a href="' + PHONE_HREF + '">' + PHONE_DISPLAY + '</a> and quote your reference.'
      };
    }
    if (mode === 'ready_to_purchase') {
      return {
        metaNote: 'nothing more is needed from you — we’re preparing your invoice now.',
        slotDetail: contactName + ' emails your invoice, ready to pay — stock is being confirmed now.',
        step3: '<strong>Once paid</strong> — we confirm your delivery date.',
        contactPara: '<strong>Your quote is with ' + contactNameHtml + '</strong> — check your inbox for the invoice. Questions before you pay? Call <a href="' + PHONE_HREF + '">' + PHONE_DISPLAY + '</a> and quote your reference.'
      };
    }
    return null; // CALL modes (asap/this_afternoon/tomorrow_morning): leave the static liquid copy as-is.
  }

  /* Populate the receipt when the main script reveals success. */
  function populateReceipt() {
    var success = root.querySelector('[data-rsi-uqb-success]');
    if (!success || success.hidden) return;
    var data = null;
    try { data = JSON.parse(sessionStorage.getItem(CFG.storageKey) || 'null'); } catch (e) {}
    if (!data) data = { ref: quoteRef, submittedAt: new Date().toISOString(), slaText: slaPromise().text, matchingExisting: '' };

    var refEl = success.querySelector('[data-rsi-p1-ref]');
    if (refEl) refEl.textContent = data.ref;
    var timeEl = success.querySelector('[data-rsi-p1-time]');
    if (timeEl) timeEl.textContent = new Date(data.submittedAt).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
    var slotEl = success.querySelector('[data-rsi-p1-slot]');
    if (slotEl) slotEl.textContent = data.callbackPreference === 'this_afternoon' ? 'This afternoon'
                                   : data.callbackPreference === 'tomorrow_morning' ? 'Tomorrow morning'
                                   : (data.slaText ? data.slaText.charAt(0).toUpperCase() + data.slaText.slice(1) : 'Within 1 working hour');
    var recapEl = success.querySelector('[data-rsi-p1-recap]');
    if (recapEl && data.summaryHtml) recapEl.innerHTML = data.summaryHtml;
    var matchingEl = success.querySelector('[data-rsi-p1-matching]');
    if (matchingEl) matchingEl.hidden = !(data.matchingExisting && data.matchingExisting.indexOf('must match') !== -1);
    var smsNote = success.querySelector('[data-rsi-p1-sms-note]');
    if (smsNote) smsNote.hidden = !data.phone;

    var copy = receiptCopy(data.callbackPreference, root.getAttribute(CONTACT_NAME_ATTR) || 'our team');
    if (copy) {
      var metaNoteEl = success.querySelector('[data-rsi-p1-meta-note]');
      if (metaNoteEl) metaNoteEl.textContent = copy.metaNote;
      var slotDetailEl = success.querySelector('[data-rsi-p1-slot-detail]');
      if (slotDetailEl) slotDetailEl.textContent = copy.slotDetail;
      var step3El = success.querySelector('[data-rsi-p1-step3]');
      if (step3El) step3El.innerHTML = copy.step3;
      var contactParaEl = success.querySelector('[data-rsi-p1-contact-para]');
      if (contactParaEl) contactParaEl.innerHTML = copy.contactPara;
    }

    try { sessionStorage.removeItem(CFG.storageKey); sessionStorage.removeItem('rsiUqbRef'); } catch (e) {}
  }
  var successEl = root.querySelector('[data-rsi-uqb-success]');
  if (successEl) {
    new MutationObserver(populateReceipt).observe(successEl, { attributes: true, attributeFilter: ['hidden'] });
    populateReceipt(); // in case the main script already revealed it
  }
})();
